import { styled } from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAuctions } from "../hooks/useAuctions";
import { useTestimonials } from "../hooks/useTestimonials";
import ProductCard from "../components/common/ProductCard";
import { Spinner } from "../components/Spinner";
import TestimonialSection from "../components/common/TestimonialSection";
import PartnersContainer from "../components/common/PartnersContainer";
import {
  determineCategory,
} from "../lib/mockWinnerData";
import { MenuItems } from "../contexts";
import {
  getAuctionPriceDisplay,
  getAuctionStatusLabel,
} from "../lib/auctionPresentation";

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
`;

const AuctionsSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 60px 60px;
  width: 100%;
  background: ${({ theme }) => theme.gradients.orange};
  box-sizing: border-box;
  min-height: 60vh;

  @media (max-width: 767px) {
    padding: 30px 20px 50px;
  }
`;

const AuctionsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  max-width: 1608px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-family: "Sofia Pro", sans-serif;
  font-weight: 600;
  font-size: 40px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors.white};
  margin: 0;

  @media (max-width: 767px) {
    font-size: 32px;
  }
`;

const AuctionsGrid = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: stretch;
  gap: 25px;
  width: 100%;
  flex-wrap: wrap;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 400px;
`;

const ErrorMessage = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  padding: 40px 20px;
`;

const EmptyState = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  padding: 60px 20px;
  width: 100%;
`;

const Auctions = () => {
  const { data: auctions, isLoading, isError } = useAuctions();
  const { data: testimonials = [] } = useTestimonials();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <PageWrapper>
        <AuctionsSection>
          <AuctionsContent>
            <PageTitle>Všechny aukce</PageTitle>
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          </AuctionsContent>
        </AuctionsSection>
        <TestimonialSection testimonials={testimonials} />
        <PartnersContainer />
      </PageWrapper>
    );
  }

  if (isError) {
    return (
      <PageWrapper>
        <AuctionsSection>
          <AuctionsContent>
            <PageTitle>Všechny aukce</PageTitle>
            <ErrorMessage>
              Nepodařilo se načíst aukce. Zkuste to prosím znovu později.
            </ErrorMessage>
          </AuctionsContent>
        </AuctionsSection>
        <TestimonialSection testimonials={testimonials} />
        <PartnersContainer />
      </PageWrapper>
    );
  }

  if (!auctions || auctions.length === 0) {
    return (
      <PageWrapper>
        <AuctionsSection>
          <AuctionsContent>
            <PageTitle>Všechny aukce</PageTitle>
            <EmptyState>
              Momentálně nejsou k dispozici žádné aukce. Zkuste to později!
            </EmptyState>
          </AuctionsContent>
        </AuctionsSection>
        <TestimonialSection testimonials={testimonials} />
        <PartnersContainer />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <AuctionsSection>
        <AuctionsContent>
          <PageTitle>Všechny aukce</PageTitle>
          <AuctionsGrid>
            {auctions
              .filter((auction) => auction.title && auction.photoUrls && auction.photoUrls.length > 0)
              .map((auction, index) => {
                const category = determineCategory(auction.title);
                const currentPrice = getAuctionPriceDisplay(auction.price);
                const imageUrl = auction.photoUrls[0];

                return (
                  <ProductCard
                    key={auction.auctionId || index}
                    productName={auction.title}
                    productLabel={category}
                    imageUrl={imageUrl}
                    soldPrice={currentPrice.full}
                    savings={getAuctionStatusLabel(auction.status)}
                    priceCaption="Aktuální cena"
                    savingsCaption="Stav aukce"
                    onClick={() => navigate(`/${MenuItems.Auctions}/${auction.auctionId}`)}
                  />
                );
              })}
          </AuctionsGrid>
        </AuctionsContent>
      </AuctionsSection>
      <TestimonialSection testimonials={testimonials} />
      <PartnersContainer />
    </PageWrapper>
  );
};

export default Auctions;
